import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import Paper from '@mui/material/Paper';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { Chip } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { Spinner } from '../../../Utility/Spinner';
import { TextInput } from '../TextInput';
import { ChipBar, typeaheadSuggestion } from './MultiselectTypeAhead.styles';

function TypeaheadSuggestion({ suggestion, index, highlightedIndex, selectedItem, inputValue }) {
  const isHighlighted = highlightedIndex === index;
  const isSelected = selectedItem && suggestion.value === selectedItem;

  const matches = match(suggestion.label, inputValue);
  const parts = parse(suggestion.label, matches);

  return (
    <MenuItem
      key={suggestion.value}
      selected={isHighlighted}
      component="div"
      style={{
        fontWeight: isSelected ? 500 : 400,
        display: 'block'
      }}
    >
      {parts.map((part, ind) =>
        part.highlight ? (
          <span key={suggestion.value + String(ind)} style={{ fontWeight: 600 }}>
            {part.text}
          </span>
        ) : (
          <strong key={suggestion.value + String(ind)} style={{ fontWeight: 300 }}>
            {part.text}
          </strong>
        )
      )}
    </MenuItem>
  );
}
TypeaheadSuggestion.propTypes = {
  suggestion: PropTypes.shape({ label: PropTypes.string, value: PropTypes.number }).isRequired,
  index: PropTypes.number.isRequired,
  highlightedIndex: PropTypes.number.isRequired,
  selectedItem: PropTypes.number,
  inputValue: PropTypes.string.isRequired
};
TypeaheadSuggestion.defaultProps = {
  selectedItem: null
};

function MultiselectTypeAhead(props) {
  const {
    theme,
    loading,
    suggestions,
    error,
    errorText,
    label,
    name,
    variant,
    required,
    disabled,
    placeholder,
    values,
    onChange,
    query
  } = props;

  const filteredSuggestions = suggestions.filter(({ value }) => values.every((val) => value !== val.value));
  const [typeMore, setTypeMore] = useState(false);

  const handleChange = (selected) => {
    onChange([selected, ...values]);
  };

  const handleSuggestionChange = (suggestion) => {
    setTypeMore(suggestion.length < 4);
    query(suggestion);
  };

  const clearOnBlur = (stateChange, downshift) => {
    const { isOpen, setState } = downshift || {};
    const allowedTypes = [
      Downshift.stateChangeTypes.mouseUp,
      Downshift.stateChangeTypes.blurInput,
      Downshift.stateChangeTypes.clickItem
    ];

    if (stateChange && allowedTypes.includes(stateChange.type) && !isOpen) {
      handleSuggestionChange('');
      setState({ inputValue: '' });
    }
  };

  const deleteSelected = (key) => {
    const filtered = values.filter(({ value }) => value !== key);
    onChange(filtered);
  };

  return (
    <>
      <Downshift
        selected={null}
        itemToString={(suggestion) => (suggestion ? suggestion.label : '')}
        onChange={handleChange}
        onInputValueChange={handleSuggestionChange}
        onStateChange={clearOnBlur}
      >
        {({ getInputProps, getItemProps, getMenuProps, highlightedIndex, inputValue, isOpen, selectedItem }) => {
          const { onChange: inputPropOnChange, onClick, onKeyDown, role, autoComplete } = getInputProps();
          const { ref } = getMenuProps();

          return (
            <div style={{ position: 'relative' }}>
              <TextInput
                InputProps={{
                  ref: getInputProps().ref,
                  autoComplete: 'off'
                }}
                error={error}
                errorText={errorText}
                variant={variant}
                label={label}
                name={name}
                required={required}
                disabled={disabled}
                placeholder={placeholder}
                onBlur={clearOnBlur}
                onChange={inputPropOnChange}
                onClick={onClick}
                onKeyDown={onKeyDown}
                role={role}
                autoComplete={autoComplete}
                value={inputValue}
              />
              <div ref={ref}>
                {isOpen ? (
                  <Paper
                    elevation={2}
                    square
                    style={{ position: 'absolute', zIndex: typeaheadSuggestion, width: '100%' }}
                  >
                    {typeMore ? (
                      <MenuItem disabled>
                        <span>Type more for results</span>
                      </MenuItem>
                    ) : (
                      <>
                        {loading && (
                          <MenuItem style={{ display: 'flex', justifyContent: 'space-around' }}>
                            <Spinner thickness={4} size={25} />
                          </MenuItem>
                        )}
                        {!loading && filteredSuggestions.length === 0 && (
                          <MenuItem disabled>
                            <span>No Results</span>
                          </MenuItem>
                        )}
                        {!loading &&
                          filteredSuggestions
                            .slice(0, 5)
                            .map((suggestion, index) => (
                              <TypeaheadSuggestion
                                suggestion={suggestion}
                                index={index}
                                itemProps={getItemProps({ item: suggestion })}
                                highlightedIndex={highlightedIndex}
                                selectedItem={selectedItem}
                                inputValue={inputValue}
                              />
                            ))}
                      </>
                    )}
                  </Paper>
                ) : null}
              </div>
            </div>
          );
        }}
      </Downshift>
      <ChipBar item xs={10}>
        {values &&
          values.map(({ label: newLabel, value }) => (
            <Chip
              style={{ backgroundColor: theme.midTheme, color: theme.lightText, margin: '5px 5px 5px 0px' }}
              key={value}
              name={newLabel}
              label={newLabel}
              onDelete={() => deleteSelected(value)}
            />
          ))}
      </ChipBar>
    </>
  );
}

MultiselectTypeAhead.defaultProps = {
  error: false,
  errorText: '',
  required: false,
  variant: 'standard',
  theme: {},
  disabled: false,
  placeholder: ''
};

MultiselectTypeAhead.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.number })).isRequired,
  values: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.number })).isRequired,
  loading: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  query: PropTypes.func.isRequired,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  required: PropTypes.bool,
  variant: PropTypes.string,
  theme: PropTypes.node,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string
};

export default MultiselectTypeAhead;
